import { FeatureGate } from '../../../features.js';
import { callMethod, createIdempotencyKey, normalizeApiResponse } from '../../../http/utils.js';
import { icon } from '../../../shared/icons/render.js';

const FALLBACK_LOCALE = 'de';
const TOAST_REGION_SELECTOR = '[data-toast-region]';

const toastState = {
  host: null,
  stack: new Map(),
  counter: 0,
};

const SNAPSHOT_CANDIDATE_KEYS = [
  'entitlements',
  'entitlements_snapshot',
  'entitlementsSnapshot',
  'snapshot',
];

const SNAPSHOT_NESTED_KEYS = [
  'module',
  'module_payload',
  'modulePayload',
  'data',
  'payload',
  'message',
  'result',
  'overview',
  'context',
];

function isPlainObject(value) {
  return value && typeof value === 'object' && !Array.isArray(value);
}

function attachEnvelope(target, source) {
  if (!target || typeof target !== 'object' || !source || typeof source !== 'object') return target;
  const envelope = {};

  if (Object.prototype.hasOwnProperty.call(source, 'success')) {
    envelope.success = source.success;
  }
  if (Object.prototype.hasOwnProperty.call(source, 'code')) {
    envelope.code = source.code;
  }
  if (typeof source.toast === 'string') {
    envelope.toast = source.toast;
  }
  if (typeof source.message === 'string') {
    envelope.message = source.message;
  } else if (typeof source.toast === 'string') {
    envelope.message = source.toast;
  }

  Object.entries(envelope).forEach(([key, value]) => {
    if (value === undefined) return;
    if (key in target) return;
    Object.defineProperty(target, key, {
      value,
      enumerable: false,
      configurable: false,
      writable: false,
    });
  });

  return target;
}

function slugifyOrganizer(candidate) {
  if (!candidate) return null;
  if (typeof candidate === 'string') {
    const normalized = candidate.trim();
    return normalized || null;
  }
  if (typeof candidate === 'object') {
    if (candidate.slug) return slugifyOrganizer(candidate.slug);
    if (candidate.name) return slugifyOrganizer(candidate.name);
    if (candidate.organizer) return slugifyOrganizer(candidate.organizer);
  }
  return null;
}

/**
 * Produces a URL-safe slug from a human title.
 *
 * Mirror of the backend implementation at
 * `cita_events/utils/slug.py::slugify_from_title`. Both sides MUST produce
 * byte-identical output for the same input — keep them in sync.
 *
 * Pipeline:
 * - German transliteration first: ä→ae, ö→oe, ü→ue, ß→ss (also Æ/Œ etc.)
 *   — NFKD alone decomposes "ö" into "o" + diacritic and drops to "o",
 *   losing the German convention "oe".
 * - NFKD + strip combining marks for other diacritics (á→a, č→c, ñ→n).
 * - Strip invisible/bidi control characters (Unicode Cf, Cc).
 * - Replace non-[a-z0-9] runs with a single hyphen; collapse + trim.
 *
 * @param {string} value
 * @returns {string}
 *
 * @example
 * slugify('Müller Event') // 'mueller-event'
 * slugify('Größe')        // 'groesse'
 * slugify('café')         // 'cafe'
 */
const _GERMAN_MAP = {
  'ä': 'ae', 'ö': 'oe', 'ü': 'ue',
  'Ä': 'Ae', 'Ö': 'Oe', 'Ü': 'Ue',
  'ß': 'ss', 'ẞ': 'SS',
  'Æ': 'Ae', 'æ': 'ae',
  'Œ': 'Oe', 'œ': 'oe',
};

function _transliterateGerman(input) {
  return input.replace(/[äöüÄÖÜßẞÆæŒœ]/g, (ch) => _GERMAN_MAP[ch] || ch);
}

export function slugify(value) {
  if (!value || typeof value !== 'string') return '';
  return _transliterateGerman(value)
    .trim()
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[\p{Cf}\p{Cc}]/gu, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/-{2,}/g, '-')
    .replace(/(^-|-$)+/g, '');
}

/**
 * Creates a debounced version of a function that delays execution
 * until after `wait` milliseconds have elapsed since the last call.
 *
 * @param {Function} fn - The function to debounce
 * @param {number} wait - The delay in milliseconds (default: 300)
 * @returns {Function} The debounced function
 *
 * @example
 * const debouncedSearch = debounce(() => search(), 300);
 * input.addEventListener('input', debouncedSearch);
 */
export function debounce(fn, wait = 300) {
  let timeoutId = null;
  return function debounced(...args) {
    if (timeoutId) clearTimeout(timeoutId);
    timeoutId = setTimeout(() => {
      fn.apply(this, args);
      timeoutId = null;
    }, wait);
  };
}

function defaultOrganizerSlug() {
  try {
    if (typeof window === 'undefined') return null;
    const boot = window.citaBootData?.organizer || window.frappe?.boot?.organizer;
    if (!boot) return null;
    return slugifyOrganizer(boot.slug || boot.name);
  } catch {
    return null;
  }
}

export function resolveOrganizerSlug(context = {}, host) {
  const hints = [
    context?.organizer,
    context?.organizer?.slug,
    context?.organizer?.name,
    typeof context?.organizer === 'string' ? context.organizer : null,
    context?.full?.organizer?.slug,
    context?.full?.organizer?.name,
    host?.dataset?.organizerSlug,
    host?.dataset?.organizer,
    defaultOrganizerSlug(),
  ];
  for (const hint of hints) {
    const slug = slugifyOrganizer(hint);
    if (slug) return slug;
  }
  return null;
}

function readArgValue(args, key) {
  if (!args || typeof key !== 'string') return null;
  if (typeof args.get === 'function') {
    try {
      return args.get(key);
    } catch {
      return null;
    }
  }
  if (isPlainObject(args)) {
    return args[key];
  }
  return null;
}

function isFeatureSnapshotCandidate(candidate) {
  if (!isPlainObject(candidate)) return false;
  if (!Object.keys(candidate).length) return false;
  if (candidate.events || candidate.features || candidate.effective || candidate.details) return true;
  const dottedKeys = Object.keys(candidate).filter((key) => typeof key === 'string' && key.includes('.'));
  return dottedKeys.length > 0;
}

function extractFeatureSnapshotFromPayload(payload) {
  if (!isPlainObject(payload)) return null;
  const queue = [payload];
  const visited = new Set();
  while (queue.length) {
    const current = queue.shift();
    if (!isPlainObject(current) || visited.has(current)) continue;
    visited.add(current);

    for (const key of SNAPSHOT_CANDIDATE_KEYS) {
      const value = current[key];
      if (isFeatureSnapshotCandidate(value)) {
        return value;
      }
    }

    const entitlements = current.entitlements || current.entitlementsSnapshot || current.entitlements_snapshot;
    if (isFeatureSnapshotCandidate(entitlements)) {
      return entitlements;
    }

    const gateSnapshot =
      current.gates?.snapshot ||
      current.gateSnapshot ||
      current.gate_snapshot ||
      current.gateSnapshot?.snapshot ||
      current.gate_snapshot?.snapshot;
    if (isFeatureSnapshotCandidate(gateSnapshot)) {
      return gateSnapshot;
    }

    if (isFeatureSnapshotCandidate(current.module?.entitlements)) {
      return current.module.entitlements;
    }

    for (const nestedKey of SNAPSHOT_NESTED_KEYS) {
      const next = current[nestedKey];
      if (isPlainObject(next)) queue.push(next);
    }
  }
  return null;
}

function syncFeatureSnapshotFromPayload(payload, options = {}) {
  if (!payload || typeof FeatureGate?.applySnapshot !== 'function') return null;
  const snapshot = extractFeatureSnapshotFromPayload(payload);
  if (!snapshot) return null;

  const organizer =
    slugifyOrganizer(options.organizer) ||
    slugifyOrganizer(payload?.organizer) ||
    slugifyOrganizer(payload?.organizer?.slug) ||
    defaultOrganizerSlug();
  if (!organizer) return null;

  try {
    return FeatureGate.applySnapshot({
      organizer,
      snapshot,
      source: options.source || 'shared.callApi',
      resolvedAt: options.resolvedAt || payload?.resolved_at || payload?.resolvedAt || null,
    });
  } catch (error) {
    if (typeof window !== 'undefined' && window.dev_server) {
      console.warn('shared.entitlements_sync_failed', error);
    }
    return null;
  }
}

function toArray(value) {
  if (!value) return [];
  if (Array.isArray(value)) return value;
  if (typeof value === 'string') {
    return value
      .split(',')
      .map((item) => item.trim())
      .filter(Boolean);
  }
  return [value];
}

function resolveHost(region) {
  if (!region && toastState.host) return toastState.host;
  const element = typeof region === 'string'
    ? document.querySelector(region)
    : region;
  if (element) return element;
  if (!toastState.host) {
    toastState.host = document.querySelector(TOAST_REGION_SELECTOR);
    if (!toastState.host) {
      toastState.host = document.createElement('div');
      toastState.host.dataset.toastRegion = '1';
      toastState.host.className = 'fixed inset-x-0 top-4 z-[60] flex flex-col items-center gap-2 px-4';
      document.body.appendChild(toastState.host);
    }
  }
  return toastState.host;
}

function normalizeLocaleCode(code) {
  if (!code) return FALLBACK_LOCALE;
  try {
    return String(code).trim().replace('_', '-').toLowerCase();
  } catch {
    return FALLBACK_LOCALE;
  }
}


/**
 * Extract a user-facing error message from a payload.
 *
 * Dual-purpose: handles both canonical envelopes ({success,code,toast,data})
 * AND raw JS Error / Frappe error objects from try/catch blocks.
 *
 * Priority chain:
 *  1. toast              — canonical envelope field (highest priority)
 *  2. error / error.toast — nested error structures
 *  3. _server_messages    — raw Frappe exception transport
 *
 * NOT read (intentional):
 *  - payload.message     — legacy contract migration (ARC-RE-004)
 *  - payload.exception / payload.exc — CWE-209 protection
 */
function decodeServerMessage(payload) {
  if (!payload) return '';
  if (typeof payload === 'string') return payload;
  // Canonical envelope field (highest priority)
  if (typeof payload.toast === 'string') return payload.toast;
  // Nested canonical error toast
  if (payload.error) {
    if (typeof payload.error === 'string') return payload.error;
    if (typeof payload.error.toast === 'string') return payload.error.toast;
  }
  // Legacy: raw Frappe exception transport (_server_messages)
  const serverMessages = payload._server_messages;
  if (Array.isArray(serverMessages) || typeof serverMessages === 'string') {
    try {
      const entries = Array.isArray(serverMessages)
        ? serverMessages
        : JSON.parse(serverMessages);
      const decoded = entries
        .map((entry) => {
          try {
            const parsed = JSON.parse(entry);
            return parsed?.message || parsed;
          } catch {
            return entry;
          }
        })
        .filter(Boolean)
        .join('\n');
      if (decoded) return decoded;
    } catch {
      // noop
    }
  }
  // payload.message intentionally NOT read (legacy contract migration)
  // payload.exception / payload.exc intentionally NOT read (CWE-209)
  return '';
}

export function resolveApiMessage(payload, fallback = '') {
  const message = decodeServerMessage(payload);
  return message || fallback || '';
}

/**
 * Surface the toast field from a Frappe HTTP-error body that the Cita
 * response middleware did not flatten — i.e. the body is
 * {message: {success: <bool>, code, toast, data}} rather than the
 * canonical top-level envelope.
 *
 * Scope is intentionally narrow (CWE-209 + ARC-RE-004):
 *  - Only read the nested .message.toast field — never .message itself
 *    as a string, never .message.exception/exc/_server_messages.
 *  - Require .message.success to be a boolean as a canonical-envelope
 *    marker, so we never surface a stringified .message body.
 *  - Caller is responsible for decoding the top-level toast first via
 *    decodeServerMessage — this helper is the strict, narrow fallback.
 */
function decodeWrappedEnvelopeToast(payload) {
  if (!payload || typeof payload !== 'object') return '';
  const nested = payload.message;
  if (!nested || typeof nested !== 'object') return '';
  if (typeof nested.success !== 'boolean') return '';
  if (typeof nested.toast !== 'string' || !nested.toast) return '';
  return nested.toast;
}

export function extractErrorMessage(error, fallback = '') {
  if (!error) return fallback || '';
  // Frappe's $.ajax-fail routes HTTP 4xx/5xx into the awaited Promise reject
  // path; error is a jqXHR whose body may be either the Cita-flattened
  // envelope ({success, code, toast, data}) — handled by decodeServerMessage —
  // or the raw Frappe wrap {message: <envelope>} when the Cita response
  // middleware did not flatten (early exception paths, non-Cita-owned
  // endpoints). decodeWrappedEnvelopeToast covers that second layout under
  // a strict canonical-envelope check.
  if (error?.responseJSON) {
    const msg = decodeServerMessage(error.responseJSON)
      || decodeWrappedEnvelopeToast(error.responseJSON);
    if (msg) return msg;
  }
  if (error?.responseText) {
    try {
      const parsed = JSON.parse(error.responseText);
      const msg = decodeServerMessage(parsed)
        || decodeWrappedEnvelopeToast(parsed);
      if (msg) return msg;
    } catch {
      // noop
    }
  }
  const msg = decodeServerMessage(error);
  if (msg) return msg;
  // JavaScript Error.message is safe (locally constructed, not server data)
  if (error instanceof Error && error.message) return error.message;
  return fallback || '';
}

/**
 * Reconstruct the canonical {success, code, toast, data} envelope from a
 * FAILED frappe.call (the jqXHR passed into a catch-block).
 *
 * Why this exists: Cita's json_err() always sets an HTTP 4xx/5xx status
 * (e.g. duplicate=409, validation_error=422, event_cancelled=409). jQuery
 * 3.x rejects the awaited promise for any non-2xx response, so EVERY Cita
 * backend error surfaces in the catch-branch as a jqXHR — never in an
 * `if (res.success) {} else {}` path. Error-driven control flow
 * (duplicate-confirm, retry, code-specific UX) MUST read the code/data
 * from here, not from the unreachable else-branch.
 *
 * Robust against both response layouts via normalizeApiResponse:
 *  - Cita-flattened top-level envelope {success, code, toast, data}
 *  - raw Frappe wrap {message: <envelope>} (non-flattened paths)
 * Mirrors the window.citaNormalizeApiMethodEnvelope contract.
 */
export function extractErrorEnvelope(error) {
  const FALLBACK = { success: false, code: 'request_failed', toast: null, data: {} };
  if (!error || typeof error !== 'object') return FALLBACK;
  let payload = error.responseJSON;
  if (!payload && typeof error.responseText === 'string') {
    try {
      payload = JSON.parse(error.responseText);
    } catch {
      payload = null;
    }
  }
  const normalized = normalizeApiResponse(payload ?? error);
  if (!normalized || typeof normalized !== 'object') return FALLBACK;
  return {
    success: normalized.success === true,
    code: normalized.code || 'request_failed',
    toast: normalized.toast || null,
    data: (normalized.data && typeof normalized.data === 'object') ? normalized.data : {},
  };
}

function extractErrorCode(res, payload) {
  if (payload && typeof payload === 'object') {
    return (
      payload.code ||
      payload.error_code ||
      payload.exc_type ||
      payload.http_status_code ||
      payload.httpCode ||
      null
    );
  }
  if (Number.isInteger(res?.status) && res.status >= 400) {
    return `http_${res.status}`;
  }
  return null;
}

function extractErrorDetails(payload) {
  if (!payload || typeof payload !== 'object') return null;
  return (
    payload.details ||
    payload.detail ||
    payload.error_details ||
    payload.errors ||
    payload.data?.errors ||
    null
  );
}

function createHttpError(res, payload, fallbackMessage) {
  const message = decodeServerMessage(payload) || fallbackMessage || `HTTP ${res?.status ?? 500}`;
  const error = new Error(message);
  error.name = payload?.exc_type || 'ApiError';
  error.status = res?.status ?? 500;
  error.response = res || null;
  error.payload = payload;
  const code = extractErrorCode(res, payload);
  if (code) error.code = code;
  const details = extractErrorDetails(payload);
  if (details) error.details = details;
  return error;
}

function attachMeta(target, res, payload) {
  if (!target || typeof target !== 'object') return target;
  const meta = {
    status: res?.status ?? 200,
    response: res,
    raw: payload,
  };
  Object.entries(meta).forEach(([key, value]) => {
    if (!(key in target)) {
      Object.defineProperty(target, key, {
        value,
        enumerable: false,
        configurable: false,
        writable: false,
      });
    }
  });
  return target;
}

/**
 * Translate text via the global frappe i18n helper with graceful fallback.
 */
export function translate(text) {
  if (typeof text !== 'string') return text;
  try {
    if (typeof window !== 'undefined' && typeof window.__ === 'function') {
      const translated = window.__(text);
      if (translated) return translated;
    }
  } catch (error) {
    console.warn('shared.translate_failed', error);
  }
  return text;
}

/**
 * Create a plain shallow object from any input while preserving defaults.
 */
export function safeObject(value, fallback = {}) {
  const base = (fallback && typeof fallback === 'object') ? { ...fallback } : {};
  if (!value || typeof value !== 'object') return base;
  return { ...base, ...value };
}

/**
 * Guarantee an array structure regardless of the incoming value.
 */
export function ensureArray(value) {
  if (!value) return [];
  return Array.isArray(value) ? value : [value];
}

/**
 * Resolve the currently active locale by inspecting the document and fallbacks.
 */
export function detectLocale(...candidates) {
  const list = [
    ...candidates,
    typeof document !== 'undefined' ? document.documentElement?.lang : null,
    navigator?.language,
    FALLBACK_LOCALE,
  ];
  for (const candidate of list) {
    const normalized = normalizeLocaleCode(candidate);
    if (normalized) return normalized;
  }
  return FALLBACK_LOCALE;
}

/**
 * Normalize a locale string into a predictable format (lowercase, dash-separated).
 */
export function normalizeLocale(code) {
  const normalized = normalizeLocaleCode(code);
  return normalized || 'default';
}

/**
 * Format a Date value using the preferred locale and Intl formatting options.
 */
export function formatWithLocale(value, options = {}, locale) {
  if (!value) return '';
  try {
    const date = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(date.getTime())) return '';
    return new Intl.DateTimeFormat(locale || detectLocale(), options).format(date);
  } catch (error) {
    console.warn('shared.formatWithLocale_failed', error);
    return '';
  }
}

/**
 * Format a full date & time string in the active locale.
 */
export function formatDateTime(value) {
  return formatWithLocale(value, {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/**
 * Format a date-only string in the active locale.
 */
export function formatDate(value) {
  return formatWithLocale(value, {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
  });
}

/**
 * Clamp text to the requested length and append ellipsis.
 */
export function clampText(value, limit = 120) {
  if (typeof value !== 'string') return '';
  if (value.length <= limit) return value;
  return `${value.slice(0, Math.max(0, limit - 1))}…`;
}

/**
 * Call a Frappe method through the shared http utils with consistent behaviour.
 */
export async function callApi(method, args = {}, options = {}) {
  const {
    headers,
    retryOnCsrf = true,
    unwrap = true,
    idempotent = true,
    autoGate = true,
    gateSource,
    organizer: organizerOption,
    organizerSlug,
    slug: slugOption,
  } = options || {};

  const requestHeaders = headers && typeof headers === 'object'
    ? { ...headers }
    : undefined;

  if (idempotent) {
    const key = requestHeaders?.['X-Idempotency-Key'];
    if (!key) {
      if (requestHeaders) {
        requestHeaders['X-Idempotency-Key'] = createIdempotencyKey();
      } else {
        // callMethod already injects a key when we do not override headers
      }
    }
  }

  const response = await callMethod(
    method,
    args,
    requestHeaders ? { headers: requestHeaders, retryOnCsrf } : { retryOnCsrf },
  );
  const { res, payload } = response || {};
  const normalized = (response && typeof response.normalized === 'object')
    ? response.normalized
    : normalizeApiResponse(payload, { response: res, url: method });
  if (!normalized.success) {
    const failurePayload = (payload && typeof payload === 'object')
      ? {
          ...payload,
          success: normalized.success,
          code: normalized.code,
          toast: normalized.toast,
          data: normalized.data,
        }
      : {
          success: normalized.success,
          code: normalized.code,
          toast: normalized.toast,
          data: normalized.data,
        };
    throw createHttpError(
      res,
      failurePayload,
      normalized.toast || translate('Unbekannter Serverfehler.'),
    );
  }
  const data = unwrap ? (normalized.data ?? {}) : payload;

  if (autoGate !== false) {
    const organizerHint =
      slugifyOrganizer(organizerOption) ||
      slugifyOrganizer(organizerSlug) ||
      slugifyOrganizer(slugOption) ||
      slugifyOrganizer(readArgValue(args, 'organizer')) ||
      slugifyOrganizer(readArgValue(args, 'organizer_slug')) ||
      slugifyOrganizer(readArgValue(args, 'slug')) ||
      defaultOrganizerSlug();
    syncFeatureSnapshotFromPayload(unwrap ? data : (normalized.data ?? {}), {
      organizer: organizerHint,
      source: gateSource || method,
    });
  }

  if (!data || typeof data !== 'object') {
    const output = attachEnvelope({ value: data }, normalized);
    return attachMeta(output, res, payload);
  }
  const output = Array.isArray(data) ? data.slice() : { ...data };
  attachEnvelope(output, normalized);
  return attachMeta(output, res, payload);
}

/**
 * Sanitize HTML by delegating to the backend-provided sanitizer when available.
 */
export async function sanitizeHtml(html, { field } = {}) {
  const value = typeof html === 'string' ? html : String(html ?? '');
  if (!value) return '';

  const globalSanitizer = window?.citaSanitizer || window?.__CITA_SANITIZER__;
  if (globalSanitizer?.sanitizeHtml) {
    return await globalSanitizer.sanitizeHtml(value, { field });
  }
  if (window?.frappe?.utils?.sanitize_html) {
    return window.frappe.utils.sanitize_html(value);
  }
  if (window?.DOMPurify?.sanitize) {
    return window.DOMPurify.sanitize(value);
  }
  // Fallback – avoid returning unsanitized HTML by stripping tags.
  const div = document.createElement('div');
  div.innerHTML = value;
  return div.textContent || '';
}

/**
 * Escape HTML entities for safe interpolation in template literals.
 * Use this for text content; use sanitizeHtml for rich HTML.
 * @param {string} str - String to escape
 * @returns {string} Escaped string safe for innerHTML
 */
export function escapeHtml(str) {
  if (str == null) return '';
  return String(str).replace(/[&<>"']/g, c => ({
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#39;',
  }[c]));
}

/**
 * ORGHUB-NOW-EG (G7-CONTENT-OPENLINK-SCHEME-GUARD): Allowlist scheme guard for
 * href sinks. Unlike navbar.sanitizeHref (same-origin only), content open-links
 * may legitimately target external destinations, so http/https/mailto are
 * allowed in addition to root-relative paths. Anything else (javascript:,
 * data:, vbscript:, protocol-relative //, parse errors) yields ''.
 * @param {string} value - Candidate URL (server/API-sourced).
 * @returns {string} The original URL if safe, otherwise ''.
 */
export function safeUrl(value) {
  if (value == null) return '';
  const trimmed = String(value).trim();
  if (!trimmed) return '';
  // ORGHUB-NOW-EG G8: backslashes -> '/' before slash-checks (browsers normalize \
  // in authority position, else "/\evil.com" bypasses the protocol-relative guard).
  const normalized = trimmed.replace(/\\/g, '/');
  // Block protocol-relative URLs (//evil.com) before they parse as same-scheme.
  if (normalized.startsWith('//')) return '';
  // Root-relative paths (e.g. /files/...) pass through untouched.
  if (normalized.startsWith('/')) return trimmed;
  try {
    const origin = (typeof location !== 'undefined' && location.origin)
      ? location.origin
      : 'http://localhost';
    const parsed = new URL(trimmed, origin);
    const protocol = (parsed.protocol || '').toLowerCase();
    if (protocol === 'http:' || protocol === 'https:' || protocol === 'mailto:') {
      return trimmed;
    }
    return '';
  } catch {
    return '';
  }
}

/**
 * Provide a toast/snackbar with stacking, optional callbacks and dismissal.
 */
export function showToast(message, tone = 'info', options = {}) {
  if (!message) return null;
  // Pass the full options object (duration | persistent | action) through.
  // Backwards-compat: a raw number is interpreted as duration.
  const forwardArg = typeof options === 'number' ? options : options;
  if (typeof window !== 'undefined' && typeof window.showToast === 'function') {
    window.showToast(message, tone, forwardArg);
    return null;
  }
  console.warn('[shared.showToast] Toast UI not available', tone, message);
  return null;
}

/**
 * Create an undo snackbar element that cooperates with the toast stack.
 * Includes a visual countdown timer indicator (D5).
 */
export function spawnUndoSnackbar(region, {
  message,
  undoLabel = translate('Rückgängig'),
  onUndo,
  onDismiss,
  token,
  expiresIn = 6000,
  tone = 'slate',
  showTimer = true,
} = {}) {
  const host = resolveHost(region);
  if (!host) return null;
  const content = message || translate('Änderung gespeichert.');

  const node = document.createElement('div');
  const identifier = `undo-${Date.now()}-${toastState.counter++}`;
  node.dataset.toastId = identifier;
  node.dataset.undoToken = token || '';
  node.dataset.tone = tone;
  node.className = 'pointer-events-auto flex w-full max-w-lg flex-col gap-2 rounded-xl border border-default-theme surface-page px-4 py-3 text-xs text-body shadow-lg backdrop-blur data-[tone=emerald]:border-emerald-500/40 data-[tone=emerald]:text-emerald-200 data-[tone=rose]:border-rose-500/40 data-[tone=rose]:text-rose-200';

  // Top row: message + button
  const topRow = document.createElement('div');
  topRow.className = 'flex items-center justify-between gap-4';

  const label = document.createElement('span');
  label.className = 'font-medium';
  label.textContent = content;

  const undoButton = document.createElement('button');
  undoButton.type = 'button';
  undoButton.dataset.undoToken = token || '';
  undoButton.className = 'rounded-lg border border-default-theme surface-elevated px-3 py-1 text-xs font-semibold text-emerald-300 transition hover:border-emerald-500 hover:text-emerald-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-400';
  undoButton.textContent = undoLabel || translate('Rückgängig');

  topRow.append(label, undoButton);
  node.appendChild(topRow);

  // Timer progress bar (D5)
  const duration = Math.min(Math.max(Number(expiresIn) * (expiresIn > 100 ? 1 : 1000), 3000), 20000);
  let animationFrame = null;
  let timerBar = null;

  if (showTimer) {
    const timerContainer = document.createElement('div');
    timerContainer.className = 'relative h-1 w-full overflow-hidden rounded-full surface-elevated';

    timerBar = document.createElement('div');
    timerBar.className = 'absolute inset-y-0 left-0 rounded-full transition-all duration-100 ease-linear';
    // Color based on tone
    if (tone === 'emerald') {
      timerBar.classList.add('bg-emerald-500/70');
    } else if (tone === 'rose') {
      timerBar.classList.add('bg-rose-500/70');
    } else {
      timerBar.classList.add('bg-slate-500/70');
    }
    timerBar.style.width = '100%';

    timerContainer.appendChild(timerBar);
    node.appendChild(timerContainer);

    // Animate the timer bar countdown
    const startTime = Date.now();
    const animate = () => {
      const elapsed = Date.now() - startTime;
      const remaining = Math.max(0, duration - elapsed);
      const percentage = (remaining / duration) * 100;
      timerBar.style.width = `${percentage}%`;

      if (remaining > 0 && node.isConnected) {
        animationFrame = requestAnimationFrame(animate);
      }
    };
    animationFrame = requestAnimationFrame(animate);
  }

  host.prepend(node);

  let timeout;
  const remove = () => {
    // Fix 5 (ISSUE-3): Clear timeout first to prevent double-remove
    if (timeout) {
      window.clearTimeout(timeout);
      timeout = null;
    }
    // Then cancel animation
    if (animationFrame) {
      cancelAnimationFrame(animationFrame);
      animationFrame = null;
    }
    // Early exit if already removed
    if (!node.isConnected) return;
    node.remove();
    toastState.stack.delete(identifier);
    if (typeof onDismiss === 'function') onDismiss({ token, element: node });
  };

  timeout = window.setTimeout(remove, duration);

  undoButton.addEventListener('click', async (event) => {
    event.preventDefault();
    window.clearTimeout(timeout);
    if (animationFrame) {
      cancelAnimationFrame(animationFrame);
      animationFrame = null;
    }
    try {
      if (typeof onUndo === 'function') {
        await onUndo({ token, element: node, event });
      }
    } finally {
      remove();
    }
  });

  toastState.stack.set(identifier, { remove });

  return node;
}

function organizerFromGates(gates) {
  if (!gates || typeof gates !== 'object') return null;
  if (gates.__organizer) return gates.__organizer;
  if (gates.organizer) return gates.organizer;
  if (gates.slug) return gates.slug;
  if (gates.__meta && typeof gates.__meta === 'object') {
    return gates.__meta.organizer || gates.__meta.slug || null;
  }
  return null;
}

function resolveGateValue(key, gates) {
  if (!key) return true;
  const normalized = String(key).trim();
  if (!normalized) return true;
  if (gates && typeof gates === 'object') {
    const containers = [
      gates,
      gates.features,
      gates.effective,
      gates.details?.features,
      gates.details?.effective,
    ];
    for (const container of containers) {
      if (!container || typeof container !== 'object') continue;
      if (!Object.prototype.hasOwnProperty.call(container, normalized)) continue;
      const entry = container[normalized];
      if (typeof entry === 'boolean') return entry;
      if (entry && typeof entry === 'object') {
        if ('allowed' in entry) return Boolean(entry.allowed);
        if ('value' in entry) return Boolean(entry.value);
        if ('enabled' in entry) return Boolean(entry.enabled);
        if ('effective' in entry) return Boolean(entry.effective);
      }
      return Boolean(entry);
    }
  }
  const organizer = organizerFromGates(gates);
  if (FeatureGate && typeof FeatureGate.hasFeature === 'function') {
    try {
      const allowed = FeatureGate.hasFeature(normalized, organizer ? { organizer } : {});
      if (typeof allowed === 'boolean') return allowed;
      const snapshot = FeatureGate.getSnapshot(organizer ? { organizer } : {});
      if (snapshot && typeof snapshot === 'object') {
        const snapshotEntry = snapshot[normalized];
        if (snapshotEntry !== undefined) {
          return Boolean(snapshotEntry && typeof snapshotEntry === 'object'
            ? snapshotEntry.allowed ?? snapshotEntry.value ?? snapshotEntry.enabled ?? snapshotEntry
            : snapshotEntry);
        }
      }
    } catch (error) {
      console.warn('shared.feature_gate_lookup_failed', normalized, error);
    }
  }
  return false;
}

export function applyFeatureHints(root, gates = {}) {
  const host = typeof root === 'string' ? document.querySelector(root) : root;
  if (!host) return;
  const elements = host.querySelectorAll('[data-feature-gate]');
  elements.forEach((element) => {
    const keys = toArray(element.dataset.featureGate);
    if (!keys.length) return;
    const allowed = keys.every((key) => resolveGateValue(key, gates));
    if (allowed) {
      element.classList.remove('opacity-40', 'pointer-events-none');
      element.removeAttribute('aria-disabled');
      element.dataset.featureLocked = 'false';
      const hint = element.querySelector('[data-feature-hint]');
      if (hint) hint.hidden = true;
      element.querySelectorAll('input, textarea, select, button').forEach((control) => {
        if (control.dataset.featureOriginalDisabled === '1') {
          control.disabled = true;
        } else if (control.dataset.featureOriginalDisabled === '0') {
          control.disabled = false;
        }
        delete control.dataset.featureOriginalDisabled;
      });
      return;
    }
    element.classList.add('opacity-40', 'pointer-events-none');
    element.setAttribute('aria-disabled', 'true');
    element.dataset.featureLocked = 'true';
    const hint = element.querySelector('[data-feature-hint]');
    const message = element.dataset.featureHint || translate('Upgrade erforderlich, um diese Funktion zu nutzen.');
    if (hint) {
      hint.hidden = false;
      hint.textContent = message;
    }
    element.querySelectorAll('input, textarea, select, button').forEach((control) => {
      if (!('featureOriginalDisabled' in control.dataset)) {
        control.dataset.featureOriginalDisabled = control.disabled ? '1' : '0';
      }
      control.disabled = true;
    });
  });
}

/**
 * Bind autosave behaviour to inputs with debouncing and lifecycle helpers.
 */
export function bindAutosave(root, {
  selector = '[data-autosave-field]',
  delay = 2000,
  getKey = (element) => `${element.dataset.autosaveSection || 'general'}:${element.dataset.autosaveField || element.name}`,
  buildPayload = (element) => ({
    field: element.dataset.autosaveField || element.name,
    section: element.dataset.autosaveSection || 'general',
    value: element.type === 'checkbox' ? element.checked : element.value,
    locale: element.dataset.autosaveLocale || null,
  }),
  onBeforeSave,
  onSave,
  onSuccess,
  onError,
} = {}) {
  if (!root || typeof onSave !== 'function') {
    return { destroy() {}, flush() {} };
  }

  const elements = Array.from(root.querySelectorAll(selector));
  const timers = new Map();
  const pending = new Map();
  const keyToElement = new Map();

  const clearTimer = (key) => {
    const timer = timers.get(key);
    if (timer) window.clearTimeout(timer);
    timers.delete(key);
  };

  // Track original values to detect actual changes
  const originalValues = new Map();

  // Helper to get current value from element
  const getValue = (element) => {
    if (element.type === 'checkbox') return element.checked;
    if (element.tagName === 'SELECT' && element.multiple) {
      return Array.from(element.selectedOptions).map(o => o.value).join(',');
    }
    return element.value;
  };

  // Initialize original values
  elements.forEach((element) => {
    const key = getKey(element);
    originalValues.set(key, getValue(element));
  });

  // v2 Premium-Polish: dispatch DOM-Events + toggle Class-States damit
  // .is-saving / .is-saved aus v2-components.css sichtbar werden und der
  // auto-polish.js Save-Bar-Status reagiert.
  // source-Tag (auto-polish.js::AUTOSAVE_SOURCE) verhindert dass DevTools
  // gefakte Events den Save-Bar-State zerruetten (Sec-Audit F-9).
  const AUTOSAVE_SOURCE = 'cita_events.shared.bindAutosave';
  const dispatchAutosaveEvent = (element, name, detail) => {
    if (!element) return;
    // source ZULETZT mergen damit caller-supplied detail.source nicht
    // ueberschreiben kann (Defense-in-Depth gegen versehentlich gefakte source).
    const finalDetail = Object.assign({}, detail || {}, { source: AUTOSAVE_SOURCE });
    const ev = new CustomEvent(`cita:autosave-${name}`, {
      detail: finalDetail,
      bubbles: true,
      cancelable: false,
    });
    try { element.dispatchEvent(ev); } catch (_) { /* no-op */ }
  };

  const setSavingState = (element, state) => {
    if (!element) return;
    element.classList.remove('is-saving', 'is-saved', 'is-error');
    if (state) element.classList.add(state);
    if (state === 'is-saving') {
      element.dataset.saving = 'true';
    } else {
      delete element.dataset.saving;
    }
    if (state === 'is-saved') {
      // .is-saved animiert sich aus (1.5s) — danach Klasse entfernen
      window.setTimeout(() => {
        element.classList.remove('is-saved');
      }, 2200);
    }
  };

  const execute = async (element, event) => {
    const key = getKey(element);
    clearTimer(key);

    // Skip if value hasn't actually changed (dirty check)
    const currentValue = getValue(element);
    const originalValue = originalValues.get(key);
    if (currentValue === originalValue) {
      // No change - skip save
      return;
    }

    if (pending.get(key)) return;
    const payload = buildPayload(element, event);
    pending.set(key, true);
    setSavingState(element, 'is-saving');
    dispatchAutosaveEvent(element, 'saving', { field: payload.field, section: payload.section });
    try {
      onBeforeSave?.(payload, { element, event });
      const result = await onSave(payload, { element, event });
      // Update original value after successful save
      originalValues.set(key, currentValue);
      onSuccess?.(result, { element, payload, event });
      setSavingState(element, 'is-saved');
      dispatchAutosaveEvent(element, 'saved', { field: payload.field, section: payload.section });
      return result;
    } catch (error) {
      onError?.(error, { element, payload, event });
      setSavingState(element, 'is-error');
      const message = error && (error.message || (typeof error === 'string' ? error : null));
      dispatchAutosaveEvent(element, 'error', {
        field: payload.field, section: payload.section, message,
      });
      throw error;
    } finally {
      pending.delete(key);
      if (!timers.has(key)) {
        keyToElement.delete(key);
      }
    }
  };

  const schedule = (element, event) => {
    const key = getKey(element);
    const wait = Number(element.dataset.autosaveDelay ?? delay);
    clearTimer(key);
    keyToElement.set(key, element);
    const timer = window.setTimeout(() => execute(element, event), Math.max(100, wait));
    timers.set(key, timer);
  };

  const listeners = elements.map((element) => {
    const handler = (event) => {
      // Für blur und change: sofort ausführen (kein Debounce nötig)
      execute(element, event);
    };

    // Standard: nur blur. Mit data-autosave-on="change": nur change (für Checkboxen)
    const events = element.dataset.autosaveOn === 'change' ? ['change'] : ['blur'];
    events.forEach((evt) => element.addEventListener(evt, handler));
    return { element, handler, events };
  });

  return {
    destroy() {
      listeners.forEach(({ element, handler, events }) => {
        events.forEach((evt) => element.removeEventListener(evt, handler));
      });
      timers.forEach((timer) => window.clearTimeout(timer));
      timers.clear();
      pending.clear();
      keyToElement.clear();
    },
    flush(element) {
      if (element) {
        return execute(element);
      }
      const unique = new Set(keyToElement.values());
      return Promise.all(Array.from(unique).map((el) => execute(el)));
    },
    schedule,
  };
}

/**
 * Bind an <input type="file"> to a handler that supports progress callbacks.
 */
export function bindImageUploader(input, {
  onUpload,
  onProgress,
  onComplete,
  onError,
  beforeUpload,
} = {}) {
  if (!input || typeof onUpload !== 'function') {
    return () => {};
  }

  const handleChange = async (event) => {
    const files = Array.from(input.files || []);
    if (!files.length) return;
    beforeUpload?.({ files, event });
    try {
      for (let index = 0; index < files.length; index += 1) {
        const file = files[index];
        const report = (progress) => onProgress?.(progress, { file, index, files });
        const result = await onUpload(file, { onProgress: report, index, files });
        onComplete?.(result, { file, index, files });
      }
    } catch (error) {
      onError?.(error, { files, event });
    } finally {
      input.value = '';
    }
  };

  input.addEventListener('change', handleChange);
  return () => input.removeEventListener('change', handleChange);
}

/**
 * Render list items with optional filter, pagination and lifecycle hooks.
 */
export function renderList(container, items, {
  renderItem,
  filter,
  map,
  sort,
  paginate,
  onEmpty,
  beforeRender,
  afterRender,
} = {}) {
  if (!container || typeof renderItem !== 'function') return [];
  const source = ensureArray(items).slice();
  let working = source;
  if (typeof filter === 'function') working = working.filter((item, index) => filter(item, index, source));
  if (typeof map === 'function') working = working.map((item, index) => map(item, index, source));
  if (typeof sort === 'function') working = working.sort(sort);

  let pagination = { page: 1, total: working.length, perPage: working.length };
  if (typeof paginate === 'function') {
    const result = paginate(working) || {};
    working = Array.isArray(result.items) ? result.items : working;
    pagination = { ...pagination, ...(result.meta || {}) };
  }

  beforeRender?.({ items: working, pagination, container });

  container.innerHTML = '';
  working.forEach((item, index) => {
    const node = renderItem(item, index, working, pagination);
    if (node) container.appendChild(node);
  });

  if (!working.length && typeof onEmpty === 'function') {
    const emptyNode = onEmpty({ container });
    if (emptyNode) container.appendChild(emptyNode);
  }

  afterRender?.({ items: working, pagination, container });
  return working;
}

/**
 * Present a confirm dialog using <dialog> when available and fall back to window.confirm.
 */
export function confirm(message, { confirmLabel, cancelLabel, tone = 'primary' } = {}) {
  return dialog({
    title: message,
    tone,
    confirmLabel: confirmLabel || translate('Bestätigen'),
    cancelLabel: cancelLabel || translate('Abbrechen'),
  });
}

/**
 * Prompt the user for input while keeping consistent styling.
 */
export function prompt(message, { placeholder = '', defaultValue = '', confirmLabel, cancelLabel } = {}) {
  return dialog({
    title: message,
    input: { placeholder, defaultValue },
    confirmLabel: confirmLabel || translate('Speichern'),
    cancelLabel: cancelLabel || translate('Abbrechen'),
  });
}

/**
 * Generic dialog helper returning a Promise that resolves to user interaction details.
 */
export function dialog({
  title,
  description,
  confirmLabel,
  cancelLabel,
  tone = 'primary',
  input,
} = {}) {
  if (typeof document === 'undefined' || typeof HTMLDialogElement === 'undefined') {
    const result = window.confirm?.(title || '') ? { confirmed: true } : { confirmed: false };
    if (result.confirmed && input) {
      const value = window.prompt?.(title || '', input.defaultValue ?? '');
      return Promise.resolve({ confirmed: value != null, value });
    }
    return Promise.resolve(result);
  }

  return new Promise((resolve) => {
    // Tone-specific configuration for buttons and icons
    const toneConfig = {
      danger: {
        buttonClass: 'bg-rose-600 hover:bg-rose-500 text-white',
        iconClass: 'text-rose-400',
        iconBg: 'bg-rose-500/10',
        icon: '<path stroke-linecap="round" stroke-linejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z" />'
      },
      warning: {
        buttonClass: 'bg-amber-600 hover:bg-amber-500 text-slate-900',
        iconClass: 'text-amber-400',
        iconBg: 'bg-amber-500/10',
        icon: '<path stroke-linecap="round" stroke-linejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z" />'
      },
      info: {
        buttonClass: 'bg-cyan-600 hover:bg-cyan-500 text-white',
        iconClass: 'text-cyan-400',
        iconBg: 'bg-cyan-500/10',
        icon: '<path stroke-linecap="round" stroke-linejoin="round" d="m11.25 11.25.041-.02a.75.75 0 0 1 1.063.852l-.708 2.836a.75.75 0 0 0 1.063.853l.041-.021M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9-3.75h.008v.008H12V8.25Z" />'
      },
      primary: {
        buttonClass: 'bg-emerald-600 hover:bg-emerald-500 text-white',
        iconClass: 'text-emerald-400',
        iconBg: 'bg-emerald-500/10',
        icon: ''
      }
    };

    const config = toneConfig[tone] || toneConfig.primary;

    const dlg = document.createElement('dialog');
    dlg.className = 'fixed inset-0 z-[10000] m-auto w-full max-w-sm h-fit rounded-2xl border border-default-theme surface-page p-0 text-heading shadow-2xl backdrop:backdrop-blur';
    dlg.dataset.tone = tone;

    const form = document.createElement('form');
    form.method = 'dialog';
    form.className = 'flex flex-col gap-4 p-6';

    // Add icon for non-primary tones
    if (title && tone !== 'primary' && config.icon) {
      const iconWrapper = document.createElement('div');
      iconWrapper.className = `flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full ${config.iconBg} mx-auto mb-3`;
      iconWrapper.innerHTML = `<svg class="h-5 w-5 ${config.iconClass}" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.5">${config.icon}</svg>`;
      form.appendChild(iconWrapper);
    }

    if (title) {
      const heading = document.createElement('h2');
      heading.className = 'text-lg font-semibold text-center';
      heading.textContent = title;
      form.appendChild(heading);
    }

    if (description) {
      const desc = document.createElement('p');
      desc.className = 'text-sm text-secondary text-center';
      desc.textContent = description;
      form.appendChild(desc);
    }

    let inputEl = null;
    if (input) {
      inputEl = document.createElement(input.multiline ? 'textarea' : 'input');
      inputEl.className = 'w-full rounded-lg border border-default-theme surface-page px-3 py-2 text-sm text-heading focus:border-emerald-500 focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500';
      inputEl.name = 'dialog-input';
      inputEl.value = input.defaultValue ?? '';
      if (input.placeholder) inputEl.placeholder = input.placeholder;
      form.appendChild(inputEl);
    }

    const actions = document.createElement('div');
    actions.className = 'flex justify-end gap-2 pt-2';

    if (cancelLabel !== null) {
      const cancelButton = document.createElement('button');
      cancelButton.type = 'submit';
      cancelButton.value = 'cancel';
      cancelButton.className = 'rounded-lg border border-default-theme px-3 py-2 text-sm text-secondary hover:border-strong-theme hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-400';
      cancelButton.textContent = cancelLabel || translate('Abbrechen');
      actions.appendChild(cancelButton);
    }

    const confirmButton = document.createElement('button');
    confirmButton.type = 'submit';
    confirmButton.value = 'confirm';
    confirmButton.className = `rounded-lg ${config.buttonClass} px-3 py-2 text-sm font-semibold shadow hover:shadow-lg transition focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-400`;
    confirmButton.textContent = confirmLabel || translate('Bestätigen');
    actions.appendChild(confirmButton);

    form.appendChild(actions);
    dlg.appendChild(form);
    document.body.appendChild(dlg);

    const cleanup = () => {
      dlg.close();
      dlg.remove();
    };

    dlg.addEventListener('close', () => {
      cleanup();
      resolve({ confirmed: false });
    }, { once: true });

    form.addEventListener('submit', (event) => {
      event.preventDefault();
      const confirmed = event.submitter?.value === 'confirm';
      const value = inputEl ? inputEl.value : undefined;
      cleanup();
      resolve({ confirmed, value });
    });

    dlg.showModal();
    inputEl?.focus?.();
  });
}


// /assets/cita_events/js/shared.js (oder slide-in-modal.js)

export class SlideInModal {
  constructor(modalId) {
    this.modalId = modalId;
    this.modal = document.querySelector(`[data-${modalId}]`);
    this.backdrop = document.querySelector(`[data-${modalId}-backdrop]`);
    this.closeBtn = document.querySelector(`[data-${modalId}-close]`);
    this.cancelBtn = document.querySelector(`[data-${modalId}-cancel]`);
    this.form = document.querySelector(`[data-${modalId}-form]`);
    this.title = document.querySelector(`[data-${modalId}-title]`);
    this.subtitle = document.querySelector(`[data-${modalId}-subtitle]`);
    
    this.init();
  }
  
  init() {
    if (!this.modal) return;
    
    // Close handlers
    this.closeBtn?.addEventListener('click', () => this.close());
    this.cancelBtn?.addEventListener('click', () => this.close());
    this.backdrop?.addEventListener('click', () => this.close());
    
    // ESC key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && !this.modal.hidden) {
        this.close();
      }
    });
  }
  
  open(options = {}) {
    if (options.title && this.title) {
      this.title.textContent = options.title;
    }
    if (options.subtitle && this.subtitle) {
      this.subtitle.textContent = options.subtitle;
    }
    
    if (options.resetForm && this.form) {
      this.form.reset();
    }
    
    if (options.data && this.form) {
      Object.entries(options.data).forEach(([key, value]) => {
        const input = this.form.querySelector(`[name="${key}"]`);
        if (input) {
          if (input.type === 'checkbox') {
            input.checked = Boolean(value);
          } else {
            input.value = value || '';
          }
        }
      });
    }
    
    this.modal.hidden = false;
    requestAnimationFrame(() => {
      this.modal.classList.remove('translate-x-full');
      this.backdrop.classList.remove('opacity-0');
    });
    
    document.body.style.overflow = 'hidden';
    
    requestAnimationFrame(() => {
      const firstInput = this.form?.querySelector('input:not([type="hidden"]), textarea, select');
      firstInput?.focus();
    });
    
    this.modal.dispatchEvent(new CustomEvent('modal:opened', { 
      detail: { modalId: this.modalId, options } 
    }));
  }
  
  close() {
    this.modal.classList.add('translate-x-full');
    this.backdrop.classList.add('opacity-0');
    
    setTimeout(() => {
      this.modal.hidden = true;
      document.body.style.overflow = '';
    }, 500);
    
    this.modal.dispatchEvent(new CustomEvent('modal:closed', { 
      detail: { modalId: this.modalId } 
    }));
  }
  
  onSubmit(callback) {
    if (this.form) {
      this.form.addEventListener('submit', (e) => {
        e.preventDefault();
        const formData = new FormData(this.form);
        callback(formData, this.form);
      });
    }
  }
  
  setLoading(isLoading) {
    const saveBtn = this.modal.querySelector(`[data-${this.modalId}-save]`);
    if (saveBtn) {
      saveBtn.disabled = isLoading;
      saveBtn.classList.toggle('opacity-50', isLoading);
      saveBtn.classList.toggle('cursor-not-allowed', isLoading);
    }
  }
  
  getData() {
    if (!this.form) return {};
    const formData = new FormData(this.form);
    return Object.fromEntries(formData);
  }
}

/**
 * Show a confirmation dialog when closing a drawer with unsaved changes.
 *
 * Name-disambiguated from `drawer-confirm.js#showDrawerConfirm` — that one is an
 * inline in-drawer confirmation (hides form, shows embedded UI). This one is a
 * standalone overlay dialog via `dialog()` for unsaved-changes prompts.
 *
 * @param {Object} options - Configuration options
 * @param {string} options.title - Dialog title (default: "Änderungen verwerfen?")
 * @param {string} options.description - Dialog description
 * @param {string} options.confirmLabel - Confirm button label (default: "Verwerfen")
 * @param {string} options.cancelLabel - Cancel button label (default: "Weiter bearbeiten")
 * @returns {Promise<{confirmed: boolean}>}
 */
export function showUnsavedChangesConfirm({
  title = translate('Änderungen verwerfen?'),
  description = translate('Du hast ungespeicherte Änderungen. Möchtest du wirklich schließen?'),
  confirmLabel = translate('Verwerfen'),
  cancelLabel = translate('Weiter bearbeiten'),
} = {}) {
  return dialog({
    title,
    description,
    confirmLabel,
    cancelLabel,
    tone: 'warning',
  });
}

/**
 * Setup inline validation for form fields.
 * @param {HTMLElement} container - The form or container element
 * @param {Object} options - Configuration options
 * @param {Object} options.rules - Validation rules per field name
 * @param {string} options.errorClass - CSS class for error state (default: 'border-rose-500')
 * @param {string} options.validClass - CSS class for valid state (default: 'border-emerald-500')
 * @param {string} options.errorSelector - Selector for error message elements (default: '[data-field-error]')
 * @param {number} options.debounceMs - Debounce time for validation (default: 300)
 * @returns {Object} Validation controller with validate(), isValid(), getErrors(), destroy() methods
 *
 * @example
 * const validator = setupInlineValidation(form, {
 *   rules: {
 *     title: { required: true, maxLength: 140 },
 *     slug: { required: true, pattern: /^[a-z0-9]+(?:-[a-z0-9]+)*$/ },
 *     email: { email: true },
 *     external_url: { url: true },
 *   }
 * });
 *
 * // Later: check if form is valid
 * if (validator.isValid()) { ... }
 *
 * // Clean up
 * validator.destroy();
 */
export function setupInlineValidation(container, {
  rules = {},
  errorClass = 'border-rose-500',
  validClass = 'border-emerald-500',
  defaultClass = 'border-default-theme',
  errorSelector = '[data-field-error]',
  debounceMs = 300,
  validateOnBlur = true,
  validateOnInput = true,
} = {}) {
  if (!container) return { validate: () => true, isValid: () => true, getErrors: () => ({}), destroy: () => {} };

  const state = {
    errors: {},
    touched: new Set(),
    listeners: [],
  };

  const validators = {
    required: (value, _param, fieldName) => {
      if (!value || (typeof value === 'string' && !value.trim())) {
        return translate('Dieses Feld ist erforderlich.');
      }
      return null;
    },
    minLength: (value, min) => {
      if (value && value.length < min) {
        return translate(`Mindestens ${min} Zeichen erforderlich.`);
      }
      return null;
    },
    maxLength: (value, max) => {
      if (value && value.length > max) {
        return translate(`Maximal ${max} Zeichen erlaubt.`);
      }
      return null;
    },
    pattern: (value, regex) => {
      if (value && !regex.test(value)) {
        return translate('Format ungültig.');
      }
      return null;
    },
    email: (value) => {
      if (value && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
        return translate('Bitte eine gültige E-Mail-Adresse eingeben.');
      }
      return null;
    },
    url: (value) => {
      if (value) {
        try {
          new URL(value);
        } catch {
          return translate('Bitte eine gültige URL eingeben (z.B. https://example.com).');
        }
      }
      return null;
    },
    slug: (value) => {
      if (value && !/^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(value)) {
        return translate('Nur Kleinbuchstaben, Zahlen und Bindestriche erlaubt.');
      }
      return null;
    },
    custom: (value, validator, fieldName, allValues) => {
      if (typeof validator === 'function') {
        return validator(value, fieldName, allValues);
      }
      return null;
    },
  };

  function getFieldValue(fieldName) {
    const field = container.querySelector(`[name="${fieldName}"]`);
    if (!field) return '';
    if (field.type === 'checkbox') return field.checked;
    if (field.type === 'file') return field.files?.[0] || null;
    return field.value || '';
  }

  function getAllValues() {
    const values = {};
    Object.keys(rules).forEach((fieldName) => {
      values[fieldName] = getFieldValue(fieldName);
    });
    return values;
  }

  function validateField(fieldName) {
    const fieldRules = rules[fieldName];
    if (!fieldRules) return null;

    const value = getFieldValue(fieldName);
    const allValues = getAllValues();

    for (const [ruleName, ruleParam] of Object.entries(fieldRules)) {
      const validate = validators[ruleName];
      if (validate) {
        const error = validate(value, ruleParam, fieldName, allValues);
        if (error) return error;
      }
    }
    return null;
  }

  function showFieldError(fieldName, error) {
    const field = container.querySelector(`[name="${fieldName}"]`);
    const errorEl = container.querySelector(`${errorSelector}[data-field="${fieldName}"], ${errorSelector}="${fieldName}"]`);

    if (field) {
      field.classList.remove(defaultClass, validClass, errorClass);
      if (error) {
        field.classList.add(errorClass);
      } else if (state.touched.has(fieldName)) {
        field.classList.add(validClass);
      } else {
        field.classList.add(defaultClass);
      }
    }

    if (errorEl) {
      if (error) {
        errorEl.textContent = error;
        errorEl.classList.remove('hidden');
      } else {
        errorEl.textContent = '';
        errorEl.classList.add('hidden');
      }
    }

    if (error) {
      state.errors[fieldName] = error;
    } else {
      delete state.errors[fieldName];
    }
  }

  function validateAllFields() {
    const errors = {};
    Object.keys(rules).forEach((fieldName) => {
      const error = validateField(fieldName);
      if (error) {
        errors[fieldName] = error;
      }
      showFieldError(fieldName, error);
      state.touched.add(fieldName);
    });
    state.errors = errors;
    return Object.keys(errors).length === 0;
  }

  function setupFieldListeners() {
    Object.keys(rules).forEach((fieldName) => {
      const field = container.querySelector(`[name="${fieldName}"]`);
      if (!field) return;

      const debouncedValidate = debounce(() => {
        state.touched.add(fieldName);
        const error = validateField(fieldName);
        showFieldError(fieldName, error);
      }, debounceMs);

      const handleBlur = () => {
        state.touched.add(fieldName);
        const error = validateField(fieldName);
        showFieldError(fieldName, error);
      };

      if (validateOnInput) {
        field.addEventListener('input', debouncedValidate);
        state.listeners.push({ field, event: 'input', handler: debouncedValidate });
      }

      if (validateOnBlur) {
        field.addEventListener('blur', handleBlur);
        state.listeners.push({ field, event: 'blur', handler: handleBlur });
      }
    });
  }

  function focusFirstError() {
    const firstErrorField = Object.keys(state.errors)[0];
    if (firstErrorField) {
      const field = container.querySelector(`[name="${firstErrorField}"]`);
      field?.focus?.();
    }
  }

  function destroy() {
    state.listeners.forEach(({ field, event, handler }) => {
      field.removeEventListener(event, handler);
    });
    state.listeners = [];
    state.errors = {};
    state.touched.clear();
  }

  function reset() {
    state.errors = {};
    state.touched.clear();
    Object.keys(rules).forEach((fieldName) => {
      showFieldError(fieldName, null);
    });
  }

  // Initialize
  setupFieldListeners();

  return {
    validate: validateAllFields,
    validateField: (fieldName) => {
      state.touched.add(fieldName);
      const error = validateField(fieldName);
      showFieldError(fieldName, error);
      return !error;
    },
    isValid: () => Object.keys(state.errors).length === 0,
    getErrors: () => ({ ...state.errors }),
    focusFirstError,
    reset,
    destroy,
  };
}

/**
 * Setup inline bulk action bar handlers.
 * @param {HTMLElement} container - The module container
 * @param {Object} options - Configuration options
 * @returns {Object} Controller with updateCount(), show(), hide(), destroy() methods
 */
export function setupInlineBulkBar(container, {
  barSelector = '[data-bulk-actions-bar]',
  countSelector = '[data-inline-bulk-count]',
  dropdownSelector = '[data-inline-dropdown]',
  onDeselect,
  onDropdownAction,
  onDelete,
} = {}) {
  if (!container) return { updateCount: () => {}, show: () => {}, hide: () => {}, destroy: () => {} };

  const bar = container.querySelector(barSelector);
  const countEl = bar?.querySelector(countSelector);
  const listeners = [];

  if (!bar) return { updateCount: () => {}, show: () => {}, hide: () => {}, destroy: () => {} };

  // Deselect all button
  const deselectBtn = bar.querySelector('[data-action="deselect-all"]');
  if (deselectBtn && onDeselect) {
    const handler = (e) => {
      e.preventDefault();
      onDeselect();
    };
    deselectBtn.addEventListener('click', handler);
    listeners.push({ el: deselectBtn, event: 'click', handler });
  }

  // Dropdown triggers
  const dropdowns = bar.querySelectorAll(dropdownSelector);
  dropdowns.forEach((dropdown) => {
    const trigger = dropdown.querySelector('[data-inline-dropdown-trigger]');
    const menu = dropdown.querySelector('[data-inline-dropdown-menu]');

    if (trigger && menu) {
      const toggleHandler = (e) => {
        e.stopPropagation();
        const isOpen = !menu.classList.contains('hidden');
        // Close all other dropdowns
        dropdowns.forEach((d) => {
          d.querySelector('[data-inline-dropdown-menu]')?.classList.add('hidden');
        });
        if (!isOpen) {
          menu.classList.remove('hidden');
        }
      };
      trigger.addEventListener('click', toggleHandler);
      listeners.push({ el: trigger, event: 'click', handler: toggleHandler });

      // Menu item clicks
      const items = menu.querySelectorAll('button[data-inline-status], button[data-inline-visibility]');
      items.forEach((item) => {
        const handler = (e) => {
          e.preventDefault();
          menu.classList.add('hidden');
          const action = item.dataset.inlineStatus !== undefined ? 'status' : 'visibility';
          const value = item.dataset.inlineStatus ?? item.dataset.inlineVisibility;
          if (onDropdownAction) {
            onDropdownAction(action, value);
          }
        };
        item.addEventListener('click', handler);
        listeners.push({ el: item, event: 'click', handler });
      });
    }
  });

  // Delete button
  const deleteBtn = bar.querySelector('[data-inline-delete]');
  if (deleteBtn && onDelete) {
    const handler = (e) => {
      e.preventDefault();
      onDelete();
    };
    deleteBtn.addEventListener('click', handler);
    listeners.push({ el: deleteBtn, event: 'click', handler });
  }

  // Close dropdowns when clicking outside
  const closeDropdowns = (e) => {
    if (!bar.contains(e.target)) {
      dropdowns.forEach((d) => {
        d.querySelector('[data-inline-dropdown-menu]')?.classList.add('hidden');
      });
    }
  };
  document.addEventListener('click', closeDropdowns);
  listeners.push({ el: document, event: 'click', handler: closeDropdowns });

  return {
    updateCount(count) {
      if (countEl) countEl.textContent = count;
      if (count > 0) {
        bar.classList.remove('hidden');
        bar.classList.add('flex');
      } else {
        bar.classList.add('hidden');
        bar.classList.remove('flex');
      }
    },
    show() {
      bar.classList.remove('hidden');
      bar.classList.add('flex');
    },
    hide() {
      bar.classList.add('hidden');
      bar.classList.remove('flex');
    },
    destroy() {
      listeners.forEach(({ el, event, handler }) => {
        el.removeEventListener(event, handler);
      });
    },
  };
}

// =================== READ-ONLY MODE HELPERS ===================

/**
 * Apply read-only mode to a module panel for terminal status (archived/cancelled).
 * - Disables all form elements (inputs, textareas, selects, buttons)
 * - Adds readonly CSS class to panel
 * - Shows "Schreibgeschützt" badge in panel header
 *
 * @param {HTMLElement} root - The module root element
 * @param {Object} context - The module context
 * @param {boolean} context.isReadOnly - Whether the module should be read-only
 * @param {string} [context.eventStatusKey] - Optional status key (e.g., 'archived', 'cancelled')
 */
export function applyReadOnlyMode(root, context) {
  if (!context?.isReadOnly) return;

  // 1. Disable all form elements (except duplicate action)
  root.querySelectorAll('input, textarea, select, button:not([data-action="duplicate"])').forEach((el) => {
    el.disabled = true;
    el.setAttribute('aria-disabled', 'true');
  });

  // 2. Add readonly class to panel (also add status-specific class if available)
  const panel = root.closest('[data-module]') || root;
  panel.classList.add('panel--readonly');
  if (context.eventStatusKey === 'cancelled') {
    panel.classList.add('panel--cancelled');
  }

  // 3. Show readonly badge in header
  const header = root.querySelector('.panel-header, .module-header, [data-panel-header], header');
  if (header && !header.querySelector('.panel-readonly-badge')) {
    const badgeHtml = `
      <span class="panel-readonly-badge">
        ${icon('lock', { size: 14 })}
        ${translate('Schreibgeschützt')}
      </span>
    `;
    header.insertAdjacentHTML('beforeend', badgeHtml);
  }

  // 4. Hide "add new" buttons (including module-specific header actions)
  root.querySelectorAll('[data-action="new"], [data-action="add"], [data-action="create"], [data-action^="add_"], [data-action^="create_"], [data-action="upload"]').forEach((btn) => {
    btn.style.display = 'none';
  });
}

/**
 * Wrapper for bindAutosave that skips binding when in read-only mode.
 * Use this instead of bindAutosave directly in modules that support read-only.
 *
 * @param {HTMLElement} root - The module root element
 * @param {Object} options - The autosave options (same as bindAutosave)
 * @param {Object} context - The module context
 * @param {boolean} context.isReadOnly - Whether the module should be read-only
 * @returns {Object} The autosave controller or a no-op controller if read-only
 */
export function bindAutosaveIfEditable(root, options, context) {
  if (context?.isReadOnly) {
    console.log('[autosave] Skipped - event is read-only');
    return { destroy() {}, flush() {}, schedule() {} };
  }
  return bindAutosave(root, options);
}

// Optional: Helper für Snackbar-Notifications
export function showSnackbar(message, type = 'info', duration = 3000) {
  const container = document.querySelector('[data-speaker-snackbar]') 
                 || document.querySelector('[data-session-snackbar]')
                 || document.body;
  
  const snackbar = document.createElement('div');
  snackbar.className = `
    inline-flex items-center gap-3 rounded-xl px-5 py-3.5 text-sm font-semibold shadow-2xl
    transform translate-y-0 opacity-100 transition-all duration-300
    ${type === 'success' ? 'bg-emerald-600 text-white border border-emerald-500' : ''}
    ${type === 'error' ? 'bg-rose-600 text-white border border-rose-500' : ''}
    ${type === 'info' ? 'bg-cyan-600 text-white border border-cyan-500' : ''}
  `;
  
  const iconMarkup = {
    success: icon('check'),
    error: icon('xMark'),
    info: icon('info'),
  }[type] || '';

  snackbar.innerHTML = `${iconMarkup}<span>${escapeHtml(message)}</span>`;
  
  container.appendChild(snackbar);
  
  setTimeout(() => {
    snackbar.classList.add('translate-y-2', 'opacity-0');
    setTimeout(() => snackbar.remove(), 300);
  }, duration);
}
